import { useEffect, useState } from "react";
import { useAuth } from "@/context/Auth";
import {
  MFA_ENABLED,
  computeMfaRequiredAfter,
  EMERGENCY_BYPASS_EMAILS,
} from "@/config/mfaFlags";
import {
  getAal,
  getMyMfaStatus,
  listFactors,
  setMfaRequiredAfter,
  markMfaEnforced,
} from "@/lib/mfa";
import MfaChallenge from "./MfaChallenge";
import MfaEnrollDialog from "./MfaEnrollDialog";

/**
 * Wraps protected routes. FAIL OPEN on any error.
 *
 * Phased enforcement:
 *   - Computes `mfa_required_after` from role/tier policy (deploy date OR account
 *     creation date as anchor, whichever is later).
 *   - Persists it on first evaluation so the deadline is stable across sessions.
 *   - Before deadline → pass through, banner shows "Required in X days".
 *   - At/after deadline (no enrollment) → blocking enrollment dialog.
 *   - Already enrolled + AAL1 → challenge.
 */
export default function MfaEnforcementGate({ children }: { children: React.ReactNode }) {
  const { session, roles, tier } = useAuth();
  const [decision, setDecision] = useState<
    | { kind: "pass" }
    | { kind: "pass-grace"; requiredAfter: string }
    | { kind: "challenge" }
    | { kind: "enroll" }
    | { kind: "loading" }
  >({ kind: "loading" });
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function evaluate() {
      try {
        if (!MFA_ENABLED || !session?.user) {
          if (!cancelled) setDecision({ kind: "pass" });
          return;
        }

        const email = (session.user.email ?? "").toLowerCase();
        const isEmergencyBypass = !!email && EMERGENCY_BYPASS_EMAILS.includes(email);

        const [{ current }, { verified: verifiedFactors }, status] = await Promise.all([
          getAal(),
          listFactors(),
          getMyMfaStatus(),
        ]);

        const isEnrolled = verifiedFactors.length > 0;

        // Already stepped up — always pass.
        if (current === "aal2") {
          if (!cancelled) setDecision({ kind: "pass" });
          return;
        }

        // Enrolled users MUST complete the challenge. Emergency bypass cannot
        // skip an enrolled user's challenge — that would leave a verified TOTP
        // factor unused and silently weaken the session.
        if (isEnrolled) {
          if (isEmergencyBypass) {
            console.warn(
              "[MfaGate] EMERGENCY_BYPASS_EMAILS hit for enrolled user — bypass IGNORED, challenge will run:",
              email,
            );
          }
          if (!cancelled) setDecision({ kind: "challenge" });
          return;
        }

        // Not enrolled + explicit env-configured emergency bypass → pass (break-glass).
        if (isEmergencyBypass) {
          console.warn(
            "[MfaGate] EMERGENCY_BYPASS_EMAILS hit for unenrolled user — MFA enforcement skipped:",
            email,
          );
          if (!cancelled) setDecision({ kind: "pass" });
          return;
        }


        // Compute deadline (null = no enforcement required for this user)
        const createdAt = session.user.created_at ? new Date(session.user.created_at) : null;
        const computed = computeMfaRequiredAfter({ email, roles, tier, accountCreatedAt: createdAt });
        if (!computed) {
          if (!cancelled) setDecision({ kind: "pass" });
          return;
        }

        // Use persisted deadline if present, otherwise persist computed one (stable across sessions).
        let deadline = status?.mfa_required_after ? new Date(status.mfa_required_after) : null;
        if (!deadline) {
          deadline = computed;
          await setMfaRequiredAfter(deadline.toISOString());
        }

        if (deadline.getTime() > Date.now()) {
          if (!cancelled) setDecision({ kind: "pass-grace", requiredAfter: deadline.toISOString() });
          return;
        }

        // Deadline passed and not enrolled → block.
        await markMfaEnforced();
        if (!cancelled) setDecision({ kind: "enroll" });
      } catch (err) {
        console.error("[MfaGate] evaluation failed — failing OPEN:", err);
        if (!cancelled) setDecision({ kind: "pass" });
      }
    }

    evaluate();
    return () => { cancelled = true; };
  }, [session?.user?.id, roles.join("|"), tier, verified]);

  if (decision.kind === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (decision.kind === "challenge") {
    return <MfaChallenge onVerified={() => setVerified((v) => !v)} />;
  }

  if (decision.kind === "enroll") {
    return (
      <MfaEnrollDialog
        open
        blocking
        onOpenChange={() => {}}
        onCompleted={() => setVerified((v) => !v)}
      />
    );
  }

  // Grace window — children render; the MfaStatusBanner shows the countdown.
  return <>{children}</>;
}
